import { ArrowUpRight, Ruler, Map, GitCompareArrows, BookOpenCheck } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ProductCta, PageHero, Reveal } from "../components/ProductPageShared";
import { usePageMeta } from "../hooks/usePageMeta";

const features = [
  {
    icon: Ruler,
    title: "Does the legal description close?",
    copy: "Automated metes-and-bounds closure checks with a plotted diagram — not a manual recheck.",
  },
  {
    icon: Map,
    title: "Schedule B-II plottability & ALTA Table A",
    copy: "Every survey exception assessed against the 2021 ALTA/NSPS Table A items.",
  },
  {
    icon: GitCompareArrows,
    title: "Legal-vs-vesting cross-check",
    copy: "Catch discrepancies between the recorded legal description and vesting deed before they become a claim.",
  },
  {
    icon: BookOpenCheck,
    title: "Grounded, cited guidance",
    copy: "Curative paths and citations for every requirement and exception — explanations only, never a substitute for counsel.",
  },
];

export default function TrueNorthPage() {
  usePageMeta({
    title: "TrueNorth — ALTA/NSPS Survey Underwriting Intelligence",
    description: "Drop in a title commitment — TrueNorth runs metes-and-bounds closure checks with plotted diagrams, grades Schedule B-II exceptions against 2021 ALTA/NSPS Table A, and cross-checks legal vs. vesting. Nationwide.",
    path: "/truenorth",
  });
  return (
    <main className="bg-navy min-h-screen" data-testid="truenorth-page">
      <Navbar />
      <div className="relative">
        <img
          src="/assets/products/marks/truenorth-mark.png"
          alt=""
          aria-hidden="true"
          className="hidden lg:block absolute top-40 right-12 w-[24rem] opacity-[0.35] pointer-events-none select-none"
        />
        <PageHero
          kicker="Product — TrueNorth"
          title="TrueNorth"
          accent="ALTA/NSPS survey underwriting intelligence."
          sub="Drop in a title commitment PDF — TrueNorth checks that the legal description closes, plots it, grades every Schedule B-II exception against ALTA Table A, and hands you cited curative paths. Nationwide coverage across all fifty states."
        >
          <div className="flex flex-wrap gap-4 mt-9">
            <a
              href="https://truenorth.nwta.app/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="truenorth-launch-btn"
              className="group relative px-7 py-4 bg-tech text-fog font-mono-accent text-[11px] tracking-[0.22em] uppercase overflow-hidden"
            >
              <span className="absolute inset-0 bg-tech-bright translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
              <span className="relative flex items-center gap-2">
                Launch TrueNorth <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform duration-300" />
              </span>
            </a>
          </div>
        </PageHero>
      </div>
      <section className="relative bg-navy pb-20 md:pb-24" data-testid="truenorth-features">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal
                key={f.title}
                index={i}
                className="group border border-steel/30 bg-navy-deep/60 p-7 md:p-9 hover:border-tech/70 transition-colors duration-500"
              >
                <Icon className="h-6 w-6 text-tech-bright" strokeWidth={1.5} />
                <h2 className="font-display font-semibold tracking-tight text-lg md:text-xl text-fog mt-5">{f.title}</h2>
                <p className="text-[13.5px] text-fog/60 leading-relaxed mt-3">{f.copy}</p>
              </Reveal>
            );
          })}
        </div>
      </section>
      <ProductCta heading="Ready to stop rechecking legals by hand?" />
      <Footer />
    </main>
  );
}
